import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

import {
  aplicarClaseTema,
  guardarPreferencia,
  leerPreferencia,
  MEDIA_OSCURO,
  type PreferenciaTema,
  resolverTemaEfectivo,
  siguientePreferencia,
  sistemaPrefiereOscuro,
  type TemaEfectivo,
} from "./theme";

interface ThemeContextValue {
  /** Preferencia explícita del usuario (claro/oscuro/sistema). */
  preferencia: PreferenciaTema;
  /** Tema realmente aplicado (resuelto). */
  temaEfectivo: TemaEfectivo;
  /** Fija una preferencia concreta. */
  setPreferencia: (pref: PreferenciaTema) => void;
  /** Avanza el ciclo claro → oscuro → sistema. */
  alternar: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Provider de modo claro/oscuro (ADR 048). Aplica la clase `.dark` en `<html>`
 * y reacciona a los cambios del SO cuando la preferencia es `sistema`. El flash
 * inicial lo evita un script inline en `index.html` que pone la clase antes del
 * primer paint; este provider la mantiene sincronizada en runtime.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preferencia, setPreferenciaState] = useState<PreferenciaTema>(() => leerPreferencia());
  const [sistemaOscuro, setSistemaOscuro] = useState<boolean>(() => sistemaPrefiereOscuro());

  // Sigue los cambios del SO (sólo relevante cuando la preferencia es "sistema",
  // pero mantenemos el estado siempre actualizado para no perder el evento).
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(MEDIA_OSCURO);
    const handler = (e: MediaQueryListEvent): void => setSistemaOscuro(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const temaEfectivo = resolverTemaEfectivo(preferencia, sistemaOscuro);

  // Sincrónico antes de pintar: evita parpadeo al cambiar de tema.
  useLayoutEffect(() => {
    aplicarClaseTema(temaEfectivo);
  }, [temaEfectivo]);

  const setPreferencia = useCallback((pref: PreferenciaTema): void => {
    guardarPreferencia(pref);
    setPreferenciaState(pref);
  }, []);

  const alternar = useCallback((): void => {
    setPreferenciaState((actual) => {
      const siguiente = siguientePreferencia(actual);
      guardarPreferencia(siguiente);
      return siguiente;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preferencia, temaEfectivo, setPreferencia, alternar }),
    [preferencia, temaEfectivo, setPreferencia, alternar],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme debe usarse dentro de <ThemeProvider>");
  return ctx;
}
